import { useMemo } from 'react';

import SelectDropdown from '@/components/Select/SelectDropdown';
import type { SelectOptionT } from '@/components/Select/types';
import PropertyValue, {
  PropertyValueStyle,
} from '@/components/Typography/PropertyValue';
import { NOT_ASSIGNED_LABEL } from '@/configs/reserved-word';
import usePopupStore from '@/stores/pure/PopupStore';
import useWorkspaceStore from '@/stores/WorkspaceStore';

export type Props = {
  selected: string;
  useShortcut?: boolean;
  name: string;
  onSelect: (key: string) => void;
  onClear?: () => void;
};

const DataOriginDropdown = (props: Props) => {
  const { workspaceFieldOptions, updateWorkspaceFieldOptions } =
    useWorkspaceStore(
      ({ workspaceFieldOptions, updateWorkspaceFieldOptions }) => ({
        workspaceFieldOptions,
        updateWorkspaceFieldOptions,
      }),
    );

  const setPopup = usePopupStore((state) => state.setPopup);

  const onDelete = (option: SelectOptionT) => {
    setPopup({
      popupTheme: 'negative',
      type: 'confirm',
      title: 'Delete the Data Origin',
      message: 'Are you sure you want to delete this Data Origin?',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      onConfirm: () => {
        updateWorkspaceFieldOptions(4, 'data_origin', option.searchName);
      },
    });
  };

  const options: SelectOptionT[] = useMemo(() => {
    return workspaceFieldOptions.data_origin_values.map((data_origin) => ({
      value: data_origin,
      searchName: data_origin,
    }));
  }, [workspaceFieldOptions.data_origin_values.join(',')]);

  const selectedItem = options.find(
    (option) => option.value === props.selected,
  );

  return (
    <SelectDropdown
      width="250px"
      name={props.name}
      searchOptions={{
        enable: true,
        placeholder: 'Search existing or add new...',
      }}
      data={options}
      value={props.selected}
      customMapper={() => {
        return selectedItem ? (
          <PropertyValueStyle>{selectedItem.searchName}</PropertyValueStyle>
        ) : (
          <PropertyValue value={NOT_ASSIGNED_LABEL} />
        );
      }}
      onSelect={(key) => props.onSelect(key as string)}
      shortcut={'KeyD'}
      onDelete={onDelete}
      onCreate={(optionName) => {
        updateWorkspaceFieldOptions(1, 'data_origin', optionName);
      }}
      onClear={props.onClear}
    />
  );
};

export default DataOriginDropdown;
